// AgentReacher subscription plans

export type SubscriptionTier = "free" | "pro" | "max";
export type PublicPlanTier = SubscriptionTier;
export type BillingPeriod = "monthly" | "yearly";
export type AccountLimit = number | "unlimited";

export interface SubscriptionDefinition {
	tier: SubscriptionTier;
	name: string;
	description: string;
	priceEUR: number;
	yearlyPriceEUR: number; // annual price (total per year)
	highlighted?: boolean;
}

export type PlanBenefitItem = {
	label: string;
	value: string | boolean;
};

export type PlanBenefitSection = {
	title: string;
	items: PlanBenefitItem[];
};

export interface PlanCapabilities {
	tier: SubscriptionTier;
	generationEnabled: boolean;
	maxStorageBytes: LimitValue;
	maxBrands: number;
	maxWorkspaces: number;
	maxConnectedSocialAccounts: AccountLimit;
	maxScheduledPosts: LimitValue;
	maxTeamMembers: number;
	schedulingEnabled: boolean;
	listeningEnabled: boolean;
	maxListeningKeywords: number;
}

export const SUBSCRIPTION_DEFINITIONS: Record<
	SubscriptionTier,
	SubscriptionDefinition
> = {
	free: {
		tier: "free",
		name: "Inactive",
		description: "No active posting plan",
		priceEUR: 0,
		yearlyPriceEUR: 0,
	},
	pro: {
		tier: "pro",
		name: "Pro",
		description: "Social media scheduler for multi-platform publishing",
		priceEUR: 19,
		yearlyPriceEUR: 190, // 10 months paid annually, 2 months free.
	},
	max: {
		tier: "max",
		name: "Max",
		description: "Social media scheduler for multi-platform publishing",
		priceEUR: 29,
		yearlyPriceEUR: 290, // 10 months paid annually, 2 months free.
		highlighted: true,
	},
};

export const PUBLIC_PRICING_TIERS = [
	"pro",
	"max",
] as const satisfies readonly Exclude<SubscriptionTier, "free">[];

export const PLAN_CAPABILITIES: Record<SubscriptionTier, PlanCapabilities> = {
	free: {
		tier: "free",
		generationEnabled: true,
		maxStorageBytes: 1024 * 1024 * 1024,
		maxBrands: 1,
		maxWorkspaces: 3,
		maxConnectedSocialAccounts: 15,
		maxScheduledPosts: 100,
		maxTeamMembers: 3,
		schedulingEnabled: true,
		listeningEnabled: false,
		maxListeningKeywords: 0,
	},
	pro: {
		tier: "pro",
		generationEnabled: true,
		maxStorageBytes: 1024 * 1024 * 1024,
		maxBrands: 1,
		maxWorkspaces: 3,
		maxConnectedSocialAccounts: 15,
		maxScheduledPosts: 100,
		maxTeamMembers: 3,
		schedulingEnabled: true,
		listeningEnabled: true,
		maxListeningKeywords: 3,
	},
	max: {
		tier: "max",
		generationEnabled: true,
		maxStorageBytes: 10 * 1024 * 1024 * 1024,
		maxBrands: 3,
		maxWorkspaces: 30,
		maxConnectedSocialAccounts: 150,
		maxScheduledPosts: 1000,
		maxTeamMembers: 30,
		schedulingEnabled: true,
		listeningEnabled: true,
		maxListeningKeywords: 3,
	},
};

export function getPlanBenefits(tier: SubscriptionTier): PlanBenefitSection[] {
	const cap = PLAN_CAPABILITIES[tier];
	const workspaceLabel = `${cap.maxWorkspaces} workspace${cap.maxWorkspaces > 1 ? "s" : ""}`;
	const teamLabel =
		cap.maxTeamMembers >= 999
			? "Unlimited team members"
			: `${cap.maxTeamMembers} team member${cap.maxTeamMembers > 1 ? "s" : ""}`;

	return [
		{
			title: "Distribution",
			items: [
				{
					label: "connected accounts",
					value: formatAccountLimit(cap.maxConnectedSocialAccounts),
				},
				{
					label: "scheduled posts / month",
					value: formatLimit(cap.maxScheduledPosts),
				},
				{ label: "Calendar scheduling", value: cap.schedulingEnabled },
				{ label: "Direct publishing", value: true },
			],
		},
		{
			title: "Content workflow",
			items: [
				{ label: "Draft and publish from chat", value: cap.generationEnabled },
				{
					label: "Per-platform captions",
					value: tier === "pro" || tier === "max",
				},
				{
					label: "Queue and calendar view",
					value: tier === "pro" || tier === "max",
				},
			],
		},
		{
			title: "Monitoring",
			items: [
				{
					label: cap.listeningEnabled
						? `Social listening (${cap.maxListeningKeywords} keywords)`
						: "Social listening (Reddit, Bluesky, YouTube)",
					value: cap.listeningEnabled,
				},
				{ label: "Daily mention digest email", value: cap.listeningEnabled },
			],
		},
		{
			title: "Platform",
			items: [
				{ label: "API & MCP access", value: true },
				{ label: workspaceLabel, value: true },
				{ label: teamLabel, value: true },
				{ label: "Email support", value: true },
				{ label: "Commercial use", value: true },
			],
		},
	];
}

export function formatPrice(priceEUR: number): string {
	if (priceEUR === 0) return "Free";
	return `€${priceEUR}`;
}

export function getMonthlyPrice(
	tier: SubscriptionTier,
	billingPeriod: BillingPeriod,
): number {
	const sub = SUBSCRIPTION_DEFINITIONS[tier];
	if (billingPeriod === "yearly") {
		return Math.round(sub.yearlyPriceEUR / 12);
	}
	return sub.priceEUR;
}

export function getYearlySavingsPercent(tier: SubscriptionTier): number {
	const sub = SUBSCRIPTION_DEFINITIONS[tier];
	if (sub.priceEUR === 0) return 0;
	const monthlyTotal = sub.priceEUR * 12;
	const savings = ((monthlyTotal - sub.yearlyPriceEUR) / monthlyTotal) * 100;
	return Math.round(savings);
}

export type LimitValue = number | "unlimited";

export function isUnlimited(value: LimitValue): value is "unlimited" {
	return value === "unlimited";
}

export function withinLimit(current: number, limit: LimitValue): boolean {
	if (isUnlimited(limit)) return true;
	return current < limit;
}

export function formatLimit(limit: LimitValue): string {
	return isUnlimited(limit) ? "∞" : String(limit);
}

export function formatAccountLimit(limit: AccountLimit): string {
	return limit === "unlimited" ? "∞" : String(limit);
}

export function getPlanCapabilities(tier: SubscriptionTier): PlanCapabilities {
	return PLAN_CAPABILITIES[tier];
}

export function getPublicPlanTier(tier: SubscriptionTier): PublicPlanTier {
	return tier;
}

export function getPublicPlanName(tier: SubscriptionTier): string {
	return SUBSCRIPTION_DEFINITIONS[tier].name;
}

export function normalizePublicPlan(
	plan: string | null | undefined,
): SubscriptionTier {
	if (!plan) return "free";

	const raw = plan.toLowerCase();
	const base = raw.replace(/-yearly$/, "");

	if (base === "free") return "free";
	if (base === "creator" || base === "starter" || base === "pro") {
		return "pro";
	}
	if (base === "business" || base === "max") {
		return "max";
	}

	return "free";
}

export interface SubscriptionRecordLike {
	plan?: string | null;
	status?: string | null;
	periodEnd?: string | Date | null;
}

const TIER_PRIORITY: Record<SubscriptionTier, number> = {
	free: 0,
	pro: 1,
	max: 2,
};

function getTimestamp(value: string | Date | null | undefined): number {
	if (!value) return 0;
	const date = value instanceof Date ? value : new Date(value);
	const timestamp = date.getTime();
	return Number.isFinite(timestamp) ? timestamp : 0;
}

function getPeriodEndTimestamp(
	periodEnd: string | Date | null | undefined,
): number {
	return getTimestamp(periodEnd);
}

export function isSubscriptionEffectiveActive(
	sub: SubscriptionRecordLike | null | undefined,
): boolean {
	return sub?.status === "active";
}

// Kept for callers that only have the status string. Prefer isSubscriptionEffectiveActive.
export function isSubscriptionActiveStatus(
	status: string | null | undefined,
): boolean {
	return status === "active";
}

export function selectBestActiveSubscription<T extends SubscriptionRecordLike>(
	subscriptions: T[] | null | undefined,
): T | undefined {
	return [...(subscriptions ?? [])]
		.filter((sub) => isSubscriptionEffectiveActive(sub))
		.sort((a, b) => {
			const tierA = normalizeSubscriptionPlan(a.plan).tier;
			const tierB = normalizeSubscriptionPlan(b.plan).tier;
			const paidDiff = Number(tierB !== "free") - Number(tierA !== "free");
			if (paidDiff !== 0) return paidDiff;

			const periodDiff =
				getPeriodEndTimestamp(b.periodEnd) - getPeriodEndTimestamp(a.periodEnd);
			if (periodDiff !== 0) return periodDiff;

			return TIER_PRIORITY[tierB] - TIER_PRIORITY[tierA];
		})[0];
}

export function normalizeSubscriptionPlan(plan: string | null | undefined): {
	tier: SubscriptionTier;
	billingPeriod: BillingPeriod;
} {
	if (!plan) return { tier: "free", billingPeriod: "monthly" };

	const raw = plan.toLowerCase();
	const billingPeriod: BillingPeriod = raw.endsWith("-yearly")
		? "yearly"
		: "monthly";
	const base = raw.replace(/-yearly$/, "");

	if (base === "pro") {
		return { tier: base, billingPeriod };
	}
	if (base === "starter" || base === "creator") {
		return { tier: "pro", billingPeriod };
	}
	if (base === "max" || base === "business") {
		return { tier: "max", billingPeriod };
	}

	return { tier: "free", billingPeriod: "monthly" };
}

export function getHigherTiers(
	currentTier: SubscriptionTier,
): Exclude<SubscriptionTier, "free">[] {
	const TIER_ORDER: SubscriptionTier[] = ["free", "pro", "max"];
	const currentIndex = TIER_ORDER.indexOf(currentTier);
	return TIER_ORDER.slice(currentIndex + 1).filter(
		(t): t is Exclude<SubscriptionTier, "free"> => t !== "free",
	);
}
